import {ReactElement, useState, useEffect, SyntheticEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {ResizableBox} from 'react-resizable';
import 'react-resizable/css/styles.css';

import {useTheme} from '@mui/material/styles';
import {
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Stack,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import {ChevronLeft} from '@mui/icons-material';
import {TreeView} from '@mui/x-tree-view';
import {TreeItem} from '@mui/x-tree-view/TreeItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import CustomTreeView from '../treeView/CustomTreeView';
import CustomDocViewer from '../docViewer/CustomDocViewer';
import {
  DocumentsContainer,
  MainContainer,
  MainTreeViewContainer,
  MainMetaInfoContainer,
  MainDocViewContainer,
  TreeViewContainer,
  MetaInfo,
  MainNoInfoContainer
} from './DocumentsComponentStyles';

import {
  getDocumentsSearchHierarchyData,
  getDocumentsHierarchyData,
  getFilesContent
} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import HelperUtils from '@shared/utils/HelperUtils';
import FormConstants from '@shared/constants/FormConstants';

import {
  AI_BLOB_SEARCH,
  BASE_MENU_ITEM,
  BASE_RECORD_ID
} from 'src/routes/BaseRoute';
import AuthUtils from 'src/utils/AuthUtils';
import EncryptUtils from 'src/utils/EncryptUtils';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import FileThumbnail from 'src/minimal-components/file-thumbnail/file-thumbnail';

import {useModuleContext} from '@context/ModuleContext';
import {useAppContext} from '@context/AppContext';

import CardComponent from '../../components/card/CardComponent';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import EmptyContent from '@components/empty-content/empty-content';
import SingleDocViewer from '@components/docViewer/SingleDocViewer';

const ClinicalMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 1,
    label: 'Encounter Number',
    data_column: 'encounter_number'
  },
  {
    id: 2,
    label: 'Encounter Date',
    data_column: 'encounter_date'
  },
  {
    id: 3,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 5,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 6,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 7,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 8,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 9,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 10,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 11,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  },
  {
    id: 12,
    label: 'Version No',
    data_column: 'version_no'
  },
  {
    id: 13,
    label: 'VersionDate',
    data_column: 'version_date',
    type: 'date-time'
  }
];
const PharmacyMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 1,
    label: 'Prescription Number',
    data_column: 'prescription_number'
  },
  {
    id: 2,
    label: 'Prescription Date',
    data_column: 'prescription_date'
  },
  {
    id: 3,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 5,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 6,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 7,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 8,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 9,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 10,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 11,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  }
];
const PAMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 1,
    label: 'Account Number',
    data_column: 'account_number'
  },
  {
    id: 2,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 3,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 4,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 5,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 6,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 7,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 8,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 9,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  }
];
const FCMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 2,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 3,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 4,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 5,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 6,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 7,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 8,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 9,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  },
  {
    id: 10,
    label: 'Version No',
    data_column: 'version_no'
  },
  {
    id: 11,
    label: 'VersionDate',
    data_column: 'version_date',
    type: 'date-time'
  }
];
const DefaultMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 2,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 3,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 4,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 5,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 6,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 7,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 8,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 9,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  },
  {
    id: 10,
    label: 'Version No',
    data_column: 'version_no'
  },
  {
    id: 11,
    label: 'VersionDate',
    data_column: 'version_date',
    type: 'date-time'
  }
];

interface Document {
  ID: number | string;
  LegacyApp: string;
  LegacyMRN: string;
  SourceDocumentID: string;
  FileExtensions: string[];
  IsNotes: boolean[];
  Name: string;
  IsMultiPage: string;
  Sequence: number[];
  DocumentTypeID: number;
  ChildNodes?: any[];
  HasDocuments?: boolean;
  ConnectionStrings: any[];
  Containers: any[];
  AccountNumber: string | null;
  EncounterNumber?: string | null;
  EncounterDate?: string | null;
  PrescriptionNumber?: string | null;
  PrescriptionDate?: string | null;
  DocumentDate: string | null;
  Owner: string | null;
  VerifiedBy: string | null;
  ReviewedBy: string | null;
  Status: any;
  SignedBy: string | null;
  UploadedBy: string | null;
  Version: string | null;
  VersionDate: string | null;
}
interface DocumentFolder {
  ID: number | string;
  ParentDocumentTypeID: null | number;
  DocumentTypeDomainID: number;
  Code: string;
  Name: string;
  Description: string;
  isActive: string;
  Sequence: string;
  FULL_PATH: string;
  ChildNodes: any[];
  HasDocuments: boolean;
  FileExtensions: [];
  SourceDocumentID?: string;
  DocumentsData?: any[];
  FilePaths?: any[];
  IsNotes?: boolean[];
}
interface DocumentsProps {
  subModuleUIData: any;
  subModuleData: any;
  module: string;
  sub_module: string;
  patient_id: string | any;
  legacyApp?: string;
  unique_id?: string;
  procedure_type?: string;
  accession_number?: string;
  specimen_number_id?:string;
  ParentLegacyValue?: string;
  folder_id?: string;
  date_range?: object;
  rowData?: any;
  height?: any;
  top?: string;
  externalSearchTerm?: string;
}

const DocumentsComponent = (props: DocumentsProps): ReactElement => {
  const theme = useTheme();
  const {t} = useTranslation();
  const [shoulddisplaymetainfo, setShoulddisplaymetainfo] =
    useState<boolean>(false);
  const [documentFolders, setDocumentFolders] = useState<DocumentFolder[]>([]);
  const [dicomFiles, setDicomFiles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [docData, setDocData] = useState<Document | null>(null);
  const [cardValues, setCardValues] = useState<any>([]);
  const [docDownloadData, setDocDownloadData] = useState<any>([]);
  const {enqueueSnackbar} = useSnackbar();
  const [iframeSrc, setIframeSrc] = useState<string | null>('');
  const [isNodeToggled, setIsNodeToggle] = useState<boolean>(false);
  const [docViewerWidth, setDocViewerWidth] = useState<number>(700);
  const [searchableDocumentFolders, setSearchableDocumentFolders] = useState<
    DocumentFolder[]
  >([]);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const {
    appState: {applicationName}
  } = useAppContext();
  const {moduleState} = useModuleContext();
  const {isBH, isBHReason}: any = moduleState;

  const menuItem = new URLSearchParams(location?.search).get(BASE_MENU_ITEM);

  const {
    subModuleUIData,
    ParentLegacyValue,
    height,
    top,
    procedure_type,
    accession_number,specimen_number_id
  } = props;
 const searchTerm = props?.externalSearchTerm ?? '';
  const recordId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const aiBlobSearch = EncryptUtils.getDecryptedData(
    location?.search,
    AI_BLOB_SEARCH
  );
  const parsedAiBlobSearch = aiBlobSearch ? JSON.parse(aiBlobSearch) : {};

  useEffect(() => {
    if (parsedAiBlobSearch?.is_ai_blob_search) {
      // Reset all states
      setShoulddisplaymetainfo(false);
      setDocumentFolders([]);
      setDicomFiles([]);
      setDocData(null);
      setCardValues([]);
      setDocDownloadData([]);
      setIframeSrc('');
      setIsNodeToggle(false);
      // setSearchTerm('');
       setSearchableDocumentFolders([]);
    }
  }, [parsedAiBlobSearch?.is_ai_blob_search]);

  const {
    documents_versions_sql: documentVersionSQL,
    document_version_data_sql: documentVersionDataSQL,
    document_data_sql: documentDataSql,
    document_folders_sql: documentFolderSql,
    legit_documents_folders_sql: legitDocumentsFoldersSql,
    documents_count_sql: documentsCountSql,
    document_folders_contents: folderContentsQuery,
    filter_schema_clause: filterSchemaClause,
    variables,
    actions,
    // show_checkbox: showCheckbox,
    hide_document_viewer: hideDocumentViewer
  } = subModuleUIData;
  const getLocaleStorageData = AuthUtils.getSelectedCheckBoxData();
 const isClinicalDocumentsViewer =
    props?.module === FormConstants.MODULE.CLINICAL &&
    subModuleUIData?.name === 'document_viewer_clinical';

  const getFilteredDicomFiles = (files: any[], searchValue: string) => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    if (!normalizedSearch) {
      return files;
    }

    return files.filter((file: any) =>
      `${file?.Filename ?? ''}`.toLowerCase().includes(normalizedSearch)
    );
  };
  useEffect(() => {
    const getDocumentFolders = async () => {
      try {
        const sql = documentFolderSql
          ? {
              documentFolderSql: `${documentFolderSql}`,
              legitDocumentFoldersSql: `${legitDocumentsFoldersSql}`,
              documentsCountSql: `${documentsCountSql}`
            }
          : {
              folderContentsQuery
            };
        const auditObj = {
          is_audit: true,
          audit_event_description: FormConstants.COMMON.VIEWED_DOCUMENT,
          rowData: props?.rowData
        };

        const tempDataColumns: any = {};

        if (props?.rowData) {
          const tempObj = props.rowData;
          for (const key in tempObj) {
            if (Object.hasOwn(tempObj, key)) {
              tempDataColumns[key] = tempObj[key];
            }
          }
        }

        const data: any = {
          sql: sql,
          variables: variables,
          filterSchemaClause: filterSchemaClause,
          // folder_id: props?.folder_id ?? '',
          date_range: props?.date_range ?? {},
          audit_identifier: AuthUtils.getAuditIdentifier(),
          audit_identifier_type: AuthUtils.getAuditIdentifierType(),
          audit_legacy_app: AuthUtils.getAuditLegacyApp(),
          isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
          isBH_reason: isBHReason,
          application_name: applicationName,
          legacyMRN: recordId,
          procedure_type,
          accession_number,specimen_number_id
        };

        data.sql_data = {...auditObj, ...tempDataColumns};

        if (ParentLegacyValue) {
          data.parentLegacyValue = ParentLegacyValue;
        }
        if (subModuleUIData.filter_schema_clause) {
          data.filter_schema_clause = subModuleUIData.filter_schema_clause;
        }
        if (
          subModuleUIData?.is_filter_sql_required &&
          getLocaleStorageData.length > 0
        ) {
          data.filter_data = getLocaleStorageData;
        }
        const encryptRequestData = EncryptUtils.encryptedData(data);
        setIsLoading(true);
        const response = documentFolderSql
          ? await getDocumentsHierarchyData(
              props?.module,
              props?.sub_module,
              props?.patient_id,
              {tk: encryptRequestData},
              props?.legacyApp,
              props?.unique_id,
              menuItem ?? ''
            )
          : await getFilesContent(
              props?.module,
              props?.sub_module,
              props?.patient_id,
              props?.unique_id || '',
              {tk: encryptRequestData},
              props?.legacyApp,
              menuItem ?? ''
            );

        const {
           dicom_files: dicomFilesResponse,
          documents_hierarchy: documentsHierarchy,
          documents: documents
        } = response;
        setDicomFiles(dicomFilesResponse);
        setDocumentFolders(documentsHierarchy ? documentsHierarchy : documents);
        setSearchableDocumentFolders([]);
        setIsLoading(false);
      } catch (e) {
        const errMessage = getErrorMessage(e);
        if (Array.isArray(documentFolders) && documentFolders.length === 0) {
          setIsLoading(false);
        } else {
          enqueueSnackbar(errMessage, SnackBarConfig.getError());
        }
      }
    };
    getDocumentFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ParentLegacyValue, parsedAiBlobSearch?.is_ai_blob_search]);
 useEffect(() => {
    const loadSearchTree = async () => {
      if (!isClinicalDocumentsViewer || !searchTerm.trim()) {
        setSearchableDocumentFolders([]);
        setIsSearchLoading(false);
        return;
      }

      try {
        setIsSearchLoading(true);
        const data: any = {
          sql: {
            documentsSearching: subModuleUIData?.documents_searching,
            folderContentsQuery
          },
          variables,
          filter_schema_clause: subModuleUIData.filter_schema_clause,
          parentLegacyValue: props?.legacyApp,
          searchTerm,
          patient_id:props?.patient_id,
        };

        const encryptRequestData = EncryptUtils.encryptedData(data);
        const response = await getDocumentsSearchHierarchyData(
          props?.module,
          props?.sub_module,
          props?.patient_id,
          {tk: encryptRequestData},
          props?.legacyApp,
          props?.unique_id,
          menuItem ?? ''
        );

        setSearchableDocumentFolders(response?.data ?? []);
      } catch (e) {
        setSearchableDocumentFolders([]);
      } finally {
        setIsSearchLoading(false);
      }
    };

    loadSearchTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isClinicalDocumentsViewer, ParentLegacyValue]);
  useEffect(() => {
    if (docData && Object.keys(docData)?.length) {
      if (setIframeSrc) {
        setIframeSrc(null);
      }
      const tempCardValues = [
        {
          name: docData?.Name,
          source_document_id: docData?.SourceDocumentID,
          account_number: docData?.AccountNumber,
          encounter_number: docData?.EncounterNumber,
          prescription_number: docData?.PrescriptionNumber,
          prescription_date: HelperUtils.formatData(
            docData?.PrescriptionDate || '',
            FormConstants.CONFIG_FIELDS_TYPE.DATE_TIME
          ),
          encounter_date: HelperUtils.formatData(
            docData?.EncounterDate || '',
            FormConstants.CONFIG_FIELDS_TYPE.DATE_TIME
          ),
          document_date: HelperUtils.formatData(
            docData?.DocumentDate || '',
            FormConstants.CONFIG_FIELDS_TYPE.DATE_TIME
          ),
          owner: docData?.Owner,
          verified_by: docData?.VerifiedBy,
          reviewed_by: docData?.ReviewedBy,
          status: docData?.Status,
          signed_by: docData?.SignedBy,
          uploaded_by: docData?.UploadedBy,
          version_no: docData?.Version,
          version_date: HelperUtils.formatData(
            docData?.VersionDate || '',
            FormConstants.CONFIG_FIELDS_TYPE.DATE_TIME
          )
        }
      ];

      setCardValues(tempCardValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docData?.SourceDocumentID, docData?.Version]);

  const getLoaderComponent = (): JSX.Element => (
    <MainNoInfoContainer height={height}>
      <LoadingScreen />
    </MainNoInfoContainer>
  );

  const handleResizeDocViewer = (e: any, data: any) => {
    const newDocViewerWidth = data.size.width;
    setDocViewerWidth(newDocViewerWidth);
  };

  const setWidth = () => {
    if (hideDocumentViewer === undefined) {
      return props.subModuleUIData.layout.browser.grid_columns;
    }

    return 12;
  };

  const getMetaInfo = () => (
    <CardComponent
      sectionUIData={{
        label: 'Document Details'
      }}
      fields={
        props?.module === FormConstants.MODULE.PATIENT_ACCOUNTING
          ? PAMetaInfoFields
          : props?.module === FormConstants.MODULE.CLINICAL
            ? ClinicalMetaInfoFields
            : props?.module === FormConstants.MODULE.PHARMACY
              ? PharmacyMetaInfoFields
              : props?.module === FormConstants.MODULE.FILECABINET
                ? FCMetaInfoFields
                : DefaultMetaInfoFields
      }
      is_colon_needed={true}
      data={cardValues}
      is_border_required={false}
      component_type="document"
      columns_per_row={1}
      module={props?.module}
      sub_module={props?.sub_module}
    />
  );

  const handleFileClick = (file: any) => {
    setIframeSrc(file);
  };

  const handleFolderChange = async (event: SyntheticEvent, nodes: string[]) => {
    setIsNodeToggle(nodes.includes('0') && !isNodeToggled);
  };
  const filteredDocumentFolders =
    isClinicalDocumentsViewer && searchTerm.trim()
      ? searchableDocumentFolders
      : documentFolders;

  const filteredDicomFiles =
    isClinicalDocumentsViewer && searchTerm.trim()
      ? getFilteredDicomFiles(dicomFiles, searchTerm)
      : dicomFiles;
  const getTreeComponent = (): JSX.Element => (
    <MainTreeViewContainer item md={setWidth()} xs={8}>
      <TreeViewContainer
        isdochidden={hideDocumentViewer}
        shoulddisplaymetainfo={`${shoulddisplaymetainfo}`}
        height={height}>
        {/* {documentFolders.map((eachNode: DocumentFolder) => ( */}
         {isSearchLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 3
            }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {filteredDocumentFolders.map((eachNode: DocumentFolder) => (
              eachNode?.ID ? (
          <CustomTreeView
            hideDocumentViewer={hideDocumentViewer}
            key={eachNode.ID}
            node={eachNode}
            setDocData={setDocData}
            docDownloadData={docDownloadData || []}
            cardValues={cardValues}
            folderContentsQuery={subModuleUIData?.document_folders_contents}
            module={props?.module}
            subModule={props?.sub_module}
            userId={props?.patient_id}
            legacyApp={props?.legacyApp}
            sqlVariables={subModuleUIData?.variables}
            unique_id={props?.unique_id}
            menuItem={menuItem || ''}
            sectionUIData={subModuleUIData}
            date_range={props?.date_range ?? {}}
            setIframeSrc={setIframeSrc}
            recordId={recordId}
            procedure_type={procedure_type}
            accession_number={accession_number}
            specimen_number_id={specimen_number_id}
            searchTerm={isClinicalDocumentsViewer ? searchTerm : ''}
          />
           ) : null
        ))}
         {filteredDicomFiles && filteredDicomFiles.length > 0 && (
          <TreeView
            aria-label="file system navigator"
            defaultCollapseIcon={
              <ArrowDropDownIcon style={{color: theme.palette.text.disabled}} />
            }
            defaultExpandIcon={
              <ArrowRightIcon style={{color: theme.palette.text.disabled}} />
            }
            defaultEndIcon={''}
            onNodeToggle={handleFolderChange}>
            <TreeItem
              nodeId="0"
              label={
                <Stack direction="row" alignItems="center" spacing={1} my={0.5}>
                  <FileThumbnail file={'folder'} sx={{width: 15, height: 15}} />
                  <Typography
                    noWrap
                    variant="inherit"
                    sx={{
                      // maxWidth: 360,
                      cursor: 'pointer',
                      textOverflow: 'clip',
                      overflow: 'unset',
                      paddingRight: '1vh',
                      color: theme.palette.common.black,
                      ...(isNodeToggled && {
                        fontWeight: 'bold'
                      })
                    }}>
                    {'Dicom Files'}
                  </Typography>
                </Stack>
              }>
             {filteredDicomFiles.map((dicomFile: any, index: any) => (
                  <TreeItem
                    key={index}
                    nodeId={dicomFile?.id}
                    label={
                      <div onClick={() => handleFileClick(dicomFile?.UrlPath)}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          my={0.5}>
                          <FileThumbnail
                            file={'file'}
                            tooltip={true}
                            sx={{width: 15, height: 15}}
                          />

                          <Typography
                            noWrap
                            variant="inherit"
                            sx={{
                              // maxWidth: 360,
                              cursor: 'pointer',
                              textOverflow: 'clip',
                              overflow: 'unset',
                              paddingRight: '1vh',
                              color: theme.palette.common.black
                            }}>
                            {dicomFile?.Filename}
                          </Typography>
                        </Stack>
                      </div>
                    }
                  />
                ))}
            </TreeItem>
          </TreeView>
                      )}
            {isClinicalDocumentsViewer &&
              searchTerm.trim() &&
              !filteredDocumentFolders.length &&
              !filteredDicomFiles?.length && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 3
                  }}>
                  <Typography variant="body2" color="text.secondary">
                    No matching files or folders found
                  </Typography>
                </Box>
              )}
          </>
        )}
      </TreeViewContainer>
      <MainMetaInfoContainer shoulddisplaymetainfo={`${shoulddisplaymetainfo}`}>
        <ListItemButton
          sx={{
            minHeight: 30,
            padding: 0,
            mb: '0.5rem'
          }}
          onClick={() => setShoulddisplaymetainfo(false)}>
          <ListItemIcon
            sx={{
              minWidth: 0,
              justifyContent: 'center'
            }}>
            <ChevronLeft color="primary" />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{
              style: {
                color: theme.palette.primary.main
              },
              fontSize: 14
            }}>
            Back to Navigation
          </ListItemText>
        </ListItemButton>
        <MetaInfo>{getMetaInfo()}</MetaInfo>
      </MainMetaInfoContainer>
    </MainTreeViewContainer>
  );

  const getDocumentViewerComponent = (): JSX.Element => (
    <ResizableBox
      width={docViewerWidth}
      axis="x" // Allow resizing horizontally only
      minConstraints={[700, Infinity]} // Min width constraint for the document viewer
      maxConstraints={[1500, Infinity]} // Max width of the document viewer based on container size
      onResize={handleResizeDocViewer} // Handle the resize event
      resizeHandles={['w']} // Only allow resizing from the left (dragging left edge)
      style={{display: 'flex', flex: 1, width: '100%'}}>
      <MainDocViewContainer
        item
        md={12}
        // md={props.subModuleUIData.layout.viewer.grid_columns}
        xs={12}>
        {iframeSrc ? (
          <Box mt={2} p={5}>
            <iframe
              src={iframeSrc}
              title={iframeSrc}
              style={{width: '52vw', height: '75vh', border: '0'}}
            />
          </Box>
        ) : (
          <>
            {!docData && isLoading && <div id="empty-div" />}
            {!docData && !isLoading && documentFolders?.length !== 0 && (
              <EmptyContent
                title="No file or folder selected"
                description="Select a file or folder to read"
                imgUrl="/assets/icons/empty/ic_email_selected.svg"
                sx={{
                  borderRadius: 1.5,
                  bgcolor: 'background.default'
                }}
              />
            )}
            {docData && !isLoading && (
              <CustomDocViewer
                docData={docData}
                setDocData={setDocData}
                documentVersionSQL={documentVersionSQL}
                documentVersionDataSQL={documentVersionDataSQL}
                documentDataSql={documentDataSql}
                variables={variables}
                filterSchemaClause={filterSchemaClause}
                docDownloadData={docDownloadData}
                shoulddisplaymetainfo={shoulddisplaymetainfo}
                setDocDownloadData={setDocDownloadData}
                setShoulddisplaymetainfo={setShoulddisplaymetainfo}
                cardValues={cardValues}
                customDocViewerActions={actions}
                PatientID={props?.patient_id}
                height={height}
                top={top}
                recordId={recordId}
                procedure_type={procedure_type}
                accession_number={accession_number}
                specimen_number_id={specimen_number_id}
              />
            )}
          </>
        )}
      </MainDocViewContainer>
    </ResizableBox>
  );

  const getTreeAndDocumentViewerComponent = (): JSX.Element => {
    if (!documentFolders.length) {
      return (
        <MainNoInfoContainer height={height}>
          <EmptyContent
            title={t('PRIVATE.DOCUMENT.NO_DATA', {ns: 'private'})}
          />
        </MainNoInfoContainer>
      );
    }

    if (!docData && !isLoading && documentFolders.length !== 0) {
      AuthUtils.clearSelectedDocID();
    }

    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%'
        }}>
        {!parsedAiBlobSearch?.is_ai_blob_search && getTreeComponent()}
        <MainContainer height={height}>
          {/* AI BLOB SEARCH */}
          {parsedAiBlobSearch?.is_ai_blob_search ? (
            <MainDocViewContainer item xs={12}>
              <SingleDocViewer
                sectionUIData={subModuleUIData?.sub_sections[0]}
                customDocViewerActions={
                  subModuleUIData?.sub_sections[0]?.actions
                }
                module={props?.module}
                sub_module={props?.sub_module}
                refresh={true}
                docDownloadData={docDownloadData}
                setDocDownloadData={setDocDownloadData}
                shoulddisplaymetainfo={shoulddisplaymetainfo}
                setShoulddisplaymetainfo={setShoulddisplaymetainfo}
                height={'50vh'}
              />
            </MainDocViewContainer>
          ) : hideDocumentViewer ? (
            ''
          ) : (
            getDocumentViewerComponent()
          )}
        </MainContainer>
      </Box>
    );
  };

  return (
    <DocumentsContainer>
      {isLoading ? getLoaderComponent() : getTreeAndDocumentViewerComponent()}
    </DocumentsContainer>
  );
};

export default DocumentsComponent;
